import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertCircle, ArrowLeft, Shield } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card.js';
import { Input } from '@/components/ui/input.js';
import { Button } from '@/components/ui/button.js';
import { useAuth } from '@/context/AuthContext.js';
import { useBranding } from '@/context/BrandingContext.js';
import { api } from '@/lib/api.js';

export function LoginPage() {
  const { branding } = useBranding();
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    setLogoError(false);
  }, [branding.logo_url]);

  const [view, setView] = useState<'login' | 'forgot' | 'forgot_success' | 'otp_verify'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // OTP Verification States
  const [challengeToken, setChallengeToken] = useState<string>('');
  const [otpEmail, setOtpEmail] = useState<string>('');
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [otpExpiresIn, setOtpExpiresIn] = useState<number>(300);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [debugOtp, setDebugOtp] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);

  // Field-level inline errors
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Loading & Submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Client-side rate-limiting / lockout (5 failed attempts -> 60s lockout)
  const [failedAttempts, setFailedAttempts] = useState(() => {
    try {
      const stored = sessionStorage.getItem('tsos_login_failed_count');
      return stored ? parseInt(stored, 10) : 0;
    } catch {
      return 0;
    }
  });
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(() => {
    try {
      const stored = sessionStorage.getItem('tsos_login_lockout_until');
      return stored ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  });
  const [countdown, setCountdown] = useState(0);

  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // OTP Countdown Timers
  useEffect(() => {
    if (view !== 'otp_verify' || otpExpiresIn <= 0) return;
    const interval = setInterval(() => {
      setOtpExpiresIn((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [view, otpExpiresIn]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Check if session expired
  const isSessionExpired = Boolean(
    (location.state as any)?.expired ||
    new URLSearchParams(location.search).get('expired') === 'true'
  );

  // Handle lockout countdown
  useEffect(() => {
    if (!lockoutUntil) {
      setCountdown(0);
      return;
    }
    const checkRemaining = () => {
      const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0) {
        setLockoutUntil(null);
        setFailedAttempts(0);
        try {
          sessionStorage.removeItem('tsos_login_lockout_until');
          sessionStorage.removeItem('tsos_login_failed_count');
        } catch {}
      }
    };
    checkRemaining();
    const interval = setInterval(checkRemaining, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const validateEmail = (val: string): boolean => {
    if (!val.trim()) {
      setEmailError('Email address is required.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val.trim())) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const validatePassword = (val: string): boolean => {
    if (!val) {
      setPasswordError('Password is required.');
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (countdown > 0) {
      setFormError(`Too many failed login attempts. Please wait ${countdown} seconds before trying again.`);
      return;
    }

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await login(email.trim(), password, rememberMe);

      // Check for Business Owner 2FA OTP challenge
      if (res?.otp_required && res.challenge_token) {
        setChallengeToken(res.challenge_token);
        setOtpEmail(res.email || email.trim());
        setOtpExpiresIn(res.expires_in || 300);
        setResendCooldown(30);
        setDebugOtp(res.debug_otp || null);
        setOtpDigits(['', '', '', '', '', '']);
        setOtpError(null);
        setResendSuccess(null);
        setView('otp_verify');
        setIsSubmitting(false);
        return;
      }

      // Reset lockout on success
      try {
        sessionStorage.removeItem('tsos_login_failed_count');
        sessionStorage.removeItem('tsos_login_lockout_until');
      } catch {}

      // Resolve redirect destination from deep link, query parameter, or saved pre-login route
      const stateFrom = (location.state as any)?.from;
      let targetPath = '/dashboard';
      if (stateFrom) {
        if (typeof stateFrom === 'string') {
          targetPath = stateFrom;
        } else if (stateFrom.pathname) {
          targetPath = `${stateFrom.pathname}${stateFrom.search || ''}`;
        }
      } else {
        const queryFrom = new URLSearchParams(location.search).get('from');
        const savedPreLogin = sessionStorage.getItem('tsos_pre_login_path');
        if (queryFrom) {
          targetPath = queryFrom;
        } else if (savedPreLogin) {
          targetPath = savedPreLogin;
        }
      }
      try {
        sessionStorage.removeItem('tsos_pre_login_path');
      } catch {}
      navigate(targetPath, { replace: true });
    } catch (err: any) {
      const newFails = failedAttempts + 1;
      setFailedAttempts(newFails);
      try {
        sessionStorage.setItem('tsos_login_failed_count', newFails.toString());
      } catch {}

      if (newFails >= 5) {
        const lockTime = Date.now() + 60 * 1000;
        setLockoutUntil(lockTime);
        try {
          sessionStorage.setItem('tsos_login_lockout_until', lockTime.toString());
        } catch {}
        setFormError('Too many failed login attempts. Account temporarily locked for 60 seconds.');
      } else {
        if (err.code === 'ACCOUNT_DEACTIVATED') {
          setFormError('This user account has been deactivated. Please contact your workspace administrator.');
        } else if (err.code === 'RATE_LIMIT_EXCEEDED' || err.status === 429) {
          setFormError(err.message || 'Too many failed attempts. Please wait before trying again.');
        } else {
          // Never reveal which field failed (email vs password)
          setFormError('Incorrect email or password.');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e?: React.FormEvent, fullCode?: string) => {
    if (e) e.preventDefault();
    setOtpError(null);
    setResendSuccess(null);

    const codeToVerify = fullCode || otpDigits.join('');
    if (codeToVerify.length !== 6) {
      setOtpError('Please enter all 6 digits of your verification code.');
      return;
    }

    if (otpExpiresIn <= 0) {
      setOtpError('Verification code has expired. Please request a new code.');
      return;
    }

    setOtpSubmitting(true);
    try {
      await verifyOtp(challengeToken, codeToVerify, rememberMe);

      // Reset lockout on success
      try {
        sessionStorage.removeItem('tsos_login_failed_count');
        sessionStorage.removeItem('tsos_login_lockout_until');
      } catch {}

      const stateFrom = (location.state as any)?.from;
      let targetPath = '/dashboard';
      if (stateFrom) {
        if (typeof stateFrom === 'string') {
          targetPath = stateFrom;
        } else if (stateFrom.pathname) {
          targetPath = `${stateFrom.pathname}${stateFrom.search || ''}`;
        }
      } else {
        const queryFrom = new URLSearchParams(location.search).get('from');
        const savedPreLogin = sessionStorage.getItem('tsos_pre_login_path');
        if (queryFrom) {
          targetPath = queryFrom;
        } else if (savedPreLogin) {
          targetPath = savedPreLogin;
        }
      }
      try {
        sessionStorage.removeItem('tsos_pre_login_path');
      } catch {}
      navigate(targetPath, { replace: true });
    } catch (err: any) {
      if (err.code === 'OTP_EXPIRED') {
        setOtpError('Verification code has expired. Please click "Resend Code" to receive a fresh code.');
      } else if (err.code === 'TOO_MANY_ATTEMPTS' || err.status === 429) {
        setOtpError('Too many failed attempts. Please return to sign in and try again.');
      } else {
        setOtpError(err.message || 'Incorrect verification code. Please check your email and try again.');
      }
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || resending) return;
    setOtpError(null);
    setResendSuccess(null);
    setResending(true);
    try {
      const res = await resendOtp(challengeToken);
      setResendCooldown(30);
      setOtpExpiresIn(300);
      if (res?.debug_otp) {
        setDebugOtp(res.debug_otp);
      }
      setResendSuccess('A new 6-digit verification code has been dispatched to your email.');
    } catch (err: any) {
      setOtpError(err.message || 'Failed to resend code. Please return to sign in.');
    } finally {
      setResending(false);
    }
  };

  const handleDigitChange = (index: number, val: string) => {
    const cleaned = val.replace(/\D/g, '');
    const newDigits = [...otpDigits];

    if (!cleaned) {
      newDigits[index] = '';
      setOtpDigits(newDigits);
      return;
    }

    const chars = cleaned.split('');
    if (chars.length > 1) {
      const startIdx = chars.length >= 6 ? 0 : index;
      for (let i = 0; i < chars.length && startIdx + i < 6; i++) {
        newDigits[startIdx + i] = chars[i];
      }
      setOtpDigits(newDigits);
      const nextFocusIdx = Math.min(5, startIdx + chars.length);
      const nextInput = document.getElementById(`otp-digit-${nextFocusIdx}`);
      nextInput?.focus();
      if (newDigits.every((d) => d.length === 1)) {
        handleOtpSubmit(undefined, newDigits.join(''));
      }
      return;
    }

    newDigits[index] = chars[0];
    setOtpDigits(newDigits);
    if (index < 5) {
      const nextInput = document.getElementById(`otp-digit-${index + 1}`);
      nextInput?.focus();
    }
    if (newDigits.every((d) => d.length === 1)) {
      handleOtpSubmit(undefined, newDigits.join(''));
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const prevInput = document.getElementById(`otp-digit-${index - 1}`);
      prevInput?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      const prevInput = document.getElementById(`otp-digit-${index - 1}`);
      prevInput?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      const nextInput = document.getElementById(`otp-digit-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasteData) return;
    const newDigits = [...otpDigits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasteData[i] || '';
    }
    setOtpDigits(newDigits);
    if (pasteData.length === 6) {
      const lastInput = document.getElementById('otp-digit-5');
      lastInput?.focus();
      handleOtpSubmit(undefined, pasteData);
    } else if (pasteData.length > 0) {
      const targetIdx = Math.min(5, pasteData.length);
      const nextInput = document.getElementById(`otp-digit-${targetIdx}`);
      nextInput?.focus();
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!validateEmail(email)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/api/v1/auth/forgot-password', { email: email.trim() });
      setView('forgot_success');
    } catch (err: any) {
      // Still show success confirmation or safe message to prevent enumeration
      setView('forgot_success');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-surface p-4 text-brand-ink">
      <Card className="w-full max-w-md shadow-lg border-brand-border bg-brand-card">
        {/* Agency Wordmark & Branding */}
        <CardHeader className="text-center space-y-2 pb-4">
          {branding.logo_url && !logoError ? (
            <div className="mx-auto flex items-center justify-center max-h-16 max-w-[220px] p-1">
              <img
                src={branding.logo_url}
                alt={branding.workspace_name || 'Agency Logo'}
                className="max-h-14 max-w-full object-contain"
                onError={() => setLogoError(true)}
              />
            </div>
          ) : (
            <div className="mx-auto h-12 w-12 rounded-xl bg-brand-teal flex items-center justify-center text-white font-bold text-xl shadow-md relative group">
              TS
              <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-brand-lime border-2 border-white" />
            </div>
          )}
          <div>
            <CardTitle className="text-xl font-bold tracking-tight text-brand-ink">
              {branding.workspace_name ? `${branding.workspace_name} Project OS` : 'Techosolution Project OS'}
            </CardTitle>
            <p className="text-xs text-brand-muted mt-1 font-medium">
              Agency Work Management &amp; Time Tracking System
            </p>
          </div>
        </CardHeader>

        {/* Session Expired Notice */}
        {isSessionExpired && view === 'login' && (
          <div className="mx-6 mb-3 flex items-center space-x-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800 border border-amber-200">
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-600" />
            <span>Your session expired — please sign in again.</span>
          </div>
        )}

        {/* Global Error Banner */}
        {formError && (
          <div className="mx-6 mb-3 flex items-center space-x-2 rounded-md bg-rose-50 p-3 text-xs text-rose-700 border border-rose-200">
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-rose-600" />
            <span>{formError}</span>
          </div>
        )}

        {/* View 1: Standard Login Form */}
        {view === 'login' && (
          <form onSubmit={handleLoginSubmit} noValidate>
            <CardContent className="space-y-4 pt-2">
              {/* Email Address */}
              <div className="space-y-1.5">
                <label htmlFor="login-email" className="text-xs font-semibold text-brand-ink">
                  Email Address
                </label>
                <Input
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="name@agency.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  onBlur={() => validateEmail(email)}
                  disabled={isSubmitting || countdown > 0}
                  className={`text-base md:text-sm min-h-[44px] ${
                    emailError ? 'border-rose-400 focus-visible:ring-rose-400' : ''
                  }`}
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? 'email-error' : undefined}
                  required
                />
                {emailError && (
                  <p id="email-error" className="text-[11px] font-medium text-rose-600 mt-0.5">
                    {emailError}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="login-password" className="text-xs font-semibold text-brand-ink">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setView('forgot');
                      setFormError(null);
                    }}
                    className="text-[11px] font-medium text-brand hover:text-brand-hover underline underline-offset-2 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (passwordError) validatePassword(e.target.value);
                    }}
                    onBlur={() => validatePassword(password)}
                    disabled={isSubmitting || countdown > 0}
                    className={`text-base md:text-sm min-h-[44px] pr-10 ${
                      passwordError ? 'border-rose-400 focus-visible:ring-rose-400' : ''
                    }`}
                    aria-invalid={Boolean(passwordError)}
                    aria-describedby={passwordError ? 'password-error' : undefined}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-brand-muted hover:text-brand-ink transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center rounded"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={0}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p id="password-error" className="text-[11px] font-medium text-rose-600 mt-0.5">
                    {passwordError}
                  </p>
                )}
              </div>

              {/* Remember Me */}
              <div className="flex items-center space-x-2 pt-1">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isSubmitting || countdown > 0}
                  className="h-4 w-4 rounded border-brand-border text-brand focus:ring-brand accent-brand"
                />
                <label htmlFor="remember-me" className="text-xs text-brand-muted select-none cursor-pointer">
                  Remember me on this device
                </label>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-3 pt-3">
              <Button
                type="submit"
                className="w-full bg-brand-teal hover:bg-teal-700 text-white font-semibold min-h-[44px] text-sm shadow transition-all active:scale-[0.99]"
                disabled={isSubmitting || countdown > 0}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing In...
                  </>
                ) : countdown > 0 ? (
                  `Locked (${countdown}s)`
                ) : (
                  'Sign In'
                )}
              </Button>
              <div className="flex items-center justify-center space-x-1.5 text-[11px] text-brand-muted">
                <Shield className="h-3.5 w-3.5 text-brand" />
                <span>Encrypted agency session with role-based access control</span>
              </div>
            </CardFooter>
          </form>
        )}

        {/* View 2: Forgot Password Request */}
        {view === 'forgot' && (
          <form onSubmit={handleForgotSubmit} noValidate>
            <CardContent className="space-y-4 pt-2">
              <div className="rounded-lg bg-brand-surface p-3 border border-brand-border text-xs text-brand-muted leading-relaxed">
                Enter your registered agency email address below. We'll send you instructions to reset your password.
              </div>
              <div className="space-y-1.5">
                <label htmlFor="forgot-email" className="text-xs font-semibold text-brand-ink">
                  Email Address
                </label>
                <Input
                  id="forgot-email"
                  type="email"
                  inputMode="email"
                  placeholder="name@agency.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  onBlur={() => validateEmail(email)}
                  disabled={isSubmitting}
                  className={`text-base md:text-sm min-h-[44px] ${
                    emailError ? 'border-rose-400 focus-visible:ring-rose-400' : ''
                  }`}
                  aria-invalid={Boolean(emailError)}
                  required
                />
                {emailError && (
                  <p className="text-[11px] font-medium text-rose-600 mt-0.5">
                    {emailError}
                  </p>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-2 pt-3">
              <Button
                type="submit"
                className="w-full bg-brand-teal hover:bg-teal-700 text-white font-semibold min-h-[44px] text-sm"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Reset Link...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setView('login');
                  setFormError(null);
                }}
                className="w-full text-brand-muted hover:text-brand-ink min-h-[40px] text-xs flex items-center justify-center space-x-1"
                disabled={isSubmitting}
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back to Sign In
              </Button>
            </CardFooter>
          </form>
        )}

        {/* View 3: Forgot Password Confirmation State */}
        {view === 'forgot_success' && (
          <div className="p-6 text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-bold text-brand-ink">Check Your Inbox</h3>
              <p className="text-xs text-brand-muted leading-relaxed">
                If an account exists for <strong className="text-brand-ink">{email}</strong>, a password reset link has been dispatched with recovery instructions.
              </p>
            </div>
            <div className="pt-2">
              <Button
                type="button"
                onClick={() => {
                  setView('login');
                  setPassword('');
                  setFormError(null);
                }}
                className="w-full bg-brand-teal hover:bg-teal-700 text-white font-medium min-h-[44px] text-sm"
              >
                Back to Sign In
              </Button>
            </div>
          </div>
        )}

        {/* View 4: Business Owner 2FA OTP Verification */}
        {view === 'otp_verify' && (
          <form onSubmit={(e) => handleOtpSubmit(e)} noValidate>
            <CardContent className="space-y-5 pt-2">
              <div className="text-center space-y-1.5">
                <div className="mx-auto h-12 w-12 rounded-full bg-teal-50 text-brand-teal border border-teal-200 flex items-center justify-center mb-3">
                  <Shield className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-brand-ink">Two-Factor Verification</h3>
                <p className="text-xs text-brand-muted leading-relaxed">
                  Enter the 6-digit security code sent to{' '}
                  <strong className="text-brand-ink">{otpEmail}</strong> to verify your business owner session.
                </p>
              </div>

              {/* OTP Error Banner */}
              {otpError && (
                <div className="flex items-center space-x-2 rounded-md bg-rose-50 p-3 text-xs text-rose-700 border border-rose-200">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 text-rose-600" />
                  <span>{otpError}</span>
                </div>
              )}

              {/* Resend Success Banner */}
              {resendSuccess && (
                <div className="flex items-center space-x-2 rounded-md bg-emerald-50 p-3 text-xs text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <span>{resendSuccess}</span>
                </div>
              )}

              {/* 6 Digit Input Slots */}
              <div className="flex justify-center items-center gap-2 sm:gap-2.5 pt-2" onPaste={handleDigitPaste}>
                {otpDigits.map((digit, idx) => (
                  <input
                    key={idx}
                    id={`otp-digit-${idx}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={digit}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => handleDigitChange(idx, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(idx, e)}
                    disabled={otpSubmitting || otpExpiresIn <= 0}
                    autoFocus={idx === 0}
                    className="w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-bold font-mono rounded-lg border border-brand-border bg-brand-surface text-brand-ink focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 outline-none transition-all disabled:opacity-50"
                  />
                ))}
              </div>

              {/* Countdown & Resend */}
              <div className="flex flex-col items-center justify-center space-y-2 pt-2 text-xs">
                {otpExpiresIn > 0 ? (
                  <p className="text-brand-muted font-medium">
                    Code expires in{' '}
                    <span className="font-mono font-bold text-brand-teal">
                      {Math.floor(otpExpiresIn / 60)
                        .toString()
                        .padStart(2, '0')}
                      :
                      {(otpExpiresIn % 60).toString().padStart(2, '0')}
                    </span>
                  </p>
                ) : (
                  <p className="text-rose-600 font-semibold">Verification code expired.</p>
                )}

                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || resending}
                    className="text-xs font-semibold text-brand-teal hover:text-teal-800 disabled:opacity-40 disabled:cursor-not-allowed underline underline-offset-2 transition-colors"
                  >
                    {resending ? 'Sending...' : resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
                  </button>
                </div>

                {debugOtp && (
                  <div className="mt-2 text-[11px] bg-teal-50 text-teal-800 border border-teal-200 px-2 py-0.5 rounded font-mono">
                    Dev / Test Code: {debugOtp}
                  </div>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-2 pt-2">
              <Button
                type="submit"
                className="w-full bg-brand-teal hover:bg-teal-700 text-white font-semibold min-h-[44px] text-sm shadow transition-all active:scale-[0.99]"
                disabled={otpSubmitting || otpDigits.some((d) => !d) || otpExpiresIn <= 0}
              >
                {otpSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setView('login');
                  setOtpError(null);
                  setResendSuccess(null);
                }}
                className="w-full text-brand-muted hover:text-brand-ink min-h-[40px] text-xs flex items-center justify-center space-x-1"
                disabled={otpSubmitting}
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back to Sign In
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}

export default LoginPage;
